/**
 * Customer Analytics Page
 * Insights from SQL Accounting customer data
 */
import { useState, useEffect } from 'react';
import { 
  Users, TrendingUp, AlertCircle, RefreshCw, DollarSign,
  Phone, Mail, Building2, CreditCard, Loader2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerAnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/analytics/customers`, { 
        withCredentials: true 
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching customer analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API_URL}/api/sql-acc/sync/customers`, {}, { 
        withCredentials: true 
      });
      await fetchData();
    } catch (error) {
      console.error('Error syncing:', error);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 text-red-700 animate-spin" />
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ms-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6" data-testid="customer-analytics-page">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
              <Users className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Customer Analytics</h1>
              <p className="text-gray-500">Insights dari SQL Accounting</p>
            </div>
          </div>
          
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from SQL'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-blue-500" />
            <span className="text-gray-500 text-sm">Total Customers</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{data?.total_customers || 0}</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-5 h-5 text-red-500" />
            <span className="text-gray-500 text-sm">Total Outstanding</span>
          </div>
          <p className="text-3xl font-bold text-red-600">{formatCurrency(data?.total_outstanding)}</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <CreditCard className="w-5 h-5 text-emerald-500" />
            <span className="text-gray-500 text-sm">Total Credit Limit</span>
          </div>
          <p className="text-3xl font-bold text-emerald-600">{formatCurrency(data?.total_credit_limit)}</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-amber-500" />
            <span className="text-gray-500 text-sm">Credit Utilization</span>
          </div>
          <p className="text-3xl font-bold text-amber-600">{(data?.utilization_rate || 0).toFixed(1)}%</p>
        </div>
      </div>

      {/* Top Customers */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Top Customers by Outstanding</h2>
        
        {data?.top_customers?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Phone</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Credit Limit</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Outstanding</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.top_customers.map((customer, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Building2 className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{customer.company_name}</p>
                          <p className="text-xs text-gray-500">{customer.sql_code}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{customer.contact_person || '-'}</td>
                    <td className="px-4 py-3">
                      {customer.phone && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Phone className="w-3 h-3" />
                          {customer.phone}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-600">
                      {formatCurrency(customer.credit_limit)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold ${customer.outstanding > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                        {formatCurrency(customer.outstanding)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{data?.message || 'No customer data available'}</p>
          </div>
        )}
      </div>

      {data?.synced_at && (
        <p className="text-xs text-gray-400 text-center">
          Last synced: {new Date(data.synced_at).toLocaleString('ms-MY')}
        </p>
      )}
    </div>
  );
};

export default CustomerAnalyticsPage;
